import { useEffect, useRef, useState } from 'react';
import styles from './Toast.module.css';

/** §5 asks for auto-dismiss; the caller picks how long, and `null` means "until dismissed". */
export const TOAST_DEFAULT_DURATION_MS = 6000;

export interface ToastProps {
    readonly open: boolean;
    /** One line, sentence case (§9). Confirms what just happened — "Sample round 2 requested". */
    readonly message: string;
    /** Fires on the Dismiss button and when the auto-dismiss timer elapses. */
    readonly onDismiss: () => void;
    /**
     * Milliseconds before auto-dismiss. `null` keeps the toast up until dismissed — use it for
     * anything the user may need to act on, since a message that leaves on its own is a message
     * some users will never read.
     */
    readonly duration?: number | null;
    readonly 'data-testid'?: string;
}

/**
 * §5's Toast — bottom-centre, `brand/deep-green` and its paired text, rendered as a `[popover]`
 * so it sits in the top layer (§4, FS-3 AC-13). Above a modal for the same reason a modal is above the
 * page: within the top layer DOM order decides, and a toast raised while a dialog is open is
 * appended after it.
 *
 * **`popover="manual"`, not `auto`, and the difference is load-bearing.** An `auto` popover
 * light-dismisses on any outside click and closes whenever another `auto` popover opens — so a
 * toast would vanish the moment the user clicked anything or opened a menu, which is precisely the
 * confirmation they were meant to read. `manual` closes only when code closes it, which is what
 * "auto-dismiss + manual close" means. It also means there is no browser-initiated close to keep
 * in sync, so unlike DropdownMenu this component needs no `toggle` listener.
 *
 * **The live region is a SEPARATE node outside the popover, and that separation is the whole
 * point.** A live region is only announced reliably when it was already in the accessibility tree
 * before its content arrived — a `role="status"` node that arrives together with its text is the
 * classic silent toast. A `[popover]` cannot host that region: while closed it computes
 * `display: none`, which takes it out of the accessibility tree entirely, so the region and its
 * words would still enter together on open. Measured in Chromium via
 * `page.accessibility.snapshot()`: a closed `[popover]` carrying `role="status"` yields ZERO
 * status nodes in the tree, and one node — already holding its text — the moment it is shown.
 * So the announcement lives in `.liveRegion`, a visually-hidden node in normal flow that is
 * mounted for the component's whole life and receives the words when `open` flips; the popover
 * keeps the visual surface and no ARIA role at all.
 *
 * **This makes staying mounted load-bearing on the CALLER.** `<Toast open={...} />` must be
 * rendered across the open flip, not conditionally mounted (`{show && <Toast …/>}`) — the latter
 * puts the region and its text back into the DOM together and reintroduces the silent toast, with
 * every test in this file still green.
 *
 * **KNOWN LIMIT — a toast raised while a modal `<dialog>` is open is VISIBLE BUT INERT.** Measured
 * in Chrome at subtask 10 and recorded as finding F-023: the toast paints correctly above the
 * dialog's backdrop, and its Dismiss button is neither hit-testable (`elementFromPoint` returns the
 * dialog) nor focusable (`focus()` is a no-op). `showModal()` makes everything outside the dialog's
 * subtree inert, and inertness is not decided by top-layer order — so the design system's §4 remedy,
 * "the toast container is mounted after the dialog root", does not fix it. The fix is a toast
 * VIEWPORT that hosts the toast inside the topmost open dialog, which is orchestration rather than a
 * primitive and is deliberately not invented here before a screen consumes one (§0's rule of two).
 * Until it exists: **a toast raised over a modal is informational only**, and it must be allowed to
 * expire — `duration={null}` over an open modal produces a toast nobody can dismiss.
 *
 * **Auto-dismiss pauses while the pointer is over the toast or focus is inside it.** Without that,
 * a toast can disappear between a user deciding to dismiss it and reaching the button — and for
 * anyone reading slowly, the timer is a deadline they never agreed to (WCAG 2.2.1's concern).
 * Unpausing restarts the full duration rather than resuming the remainder: the user has just
 * looked away, so the useful clock is "time since they stopped reading", not what was left before.
 */
export function Toast({
    open,
    message,
    onDismiss,
    duration = TOAST_DEFAULT_DURATION_MS,
    'data-testid': testId,
}: ToastProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    const [paused, setPaused] = useState(false);

    // Held in a ref so the timer below depends on the TIMING inputs only. A caller that passes an
    // inline arrow (which is most of them) re-creates `onDismiss` every render, and depending on it
    // directly would restart the countdown on each one — a toast that never dismisses itself.
    const onDismissRef = useRef(onDismiss);
    onDismissRef.current = onDismiss;

    useEffect(() => {
        const root = rootRef.current;
        if (root === null) {
            return;
        }
        if (open) {
            root.showPopover();
        } else {
            root.hidePopover();
            // A toast that closed while the pointer happened to be over it would otherwise come
            // back paused, and the next one would sit there until dismissed by hand.
            setPaused(false);
        }
    }, [open]);

    useEffect(() => {
        if (!open || duration === null || paused) {
            return;
        }
        const timer = setTimeout(() => onDismissRef.current(), duration);
        return () => clearTimeout(timer);
    }, [open, duration, paused]);

    return (
        <>
            {/*
             * The announcement. Outside the popover and never unmounted, so it is in the
             * accessibility tree before it has anything to say — see the note on the component.
             * `aria-hidden` must NOT be added here and the node must not be `hidden`: either would
             * remove it from the tree and take the announcement with it. It carries the message
             * only, not the Dismiss button, because a live region should read the confirmation
             * rather than enumerate the controls beside it.
             */}
            <div
                className={styles.liveRegion}
                data-testid={testId === undefined ? undefined : `${testId}-live`}
                role="status"
            >
                {open ? message : ''}
            </div>
            {/*
             * The four handlers below are the auto-dismiss pause, and they are why this element
             * carries interactions while remaining a plain div rather than becoming a control.
             * Biome asks for a semantically interactive element instead; taking that advice would
             * mean making the toast surface itself a button, which it is not — the Dismiss button
             * inside it is. The rule's real concern — an interaction only a mouse can reach — does
             * not apply: the focus pair is the keyboard equivalent of the pointer pair, which is
             * the point of having both.
             *
             * TWO suppressions, and both are needed rather than one being redundant: Biome reports
             * these two rules one at a time, so suppressing either alone simply surfaces the other
             * on the same element. Before the live region moved off this div it had `role="status"`
             * and only the non-interactive rule fired; a role-less div with handlers trips the
             * static rule as well. Suppress one and the next person sees a fresh red lane and no
             * explanation for it.
             */}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: see the note directly above */}
            {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: see the note directly above */}
            <div
                className={styles.toast}
                data-testid={testId}
                onBlur={(event) => {
                    // `onBlur`/`onFocus` rather than the focusin pair: React's versions already
                    // bubble, and `relatedTarget` is what tells a move WITHIN the toast from a
                    // move out of it.
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                        setPaused(false);
                    }
                }}
                onFocus={() => setPaused(true)}
                onPointerEnter={() => setPaused(true)}
                onPointerLeave={() => setPaused(false)}
                popover="manual"
                ref={rootRef}
            >
                {open ? (
                    <>
                        <p className={styles.message}>{message}</p>
                        <button
                            className={styles.dismiss}
                            data-testid={testId === undefined ? undefined : `${testId}-dismiss`}
                            onClick={onDismiss}
                            type="button"
                        >
                            Dismiss
                        </button>
                    </>
                ) : null}
            </div>
        </>
    );
}
